import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { GroupedInventoryTable } from "../../../components/BI/GroupedInventoryTable";
import { PaginationControls } from "../../../components/BI/GroupedInventoryTable/PaginationControls";
import FilterBar from "../../../components/BI/FilterBar";
import ExportButtons from "../../../components/BI/ExportButtons";
import PollingIndicator from "../../../components/BI/PollingIndicator";
import usePolling from "../../../hooks/usePolling";
import {
  fetchInventarioResumen,
  setInventarioResumenFilters,
  setInventarioResumenPagination,
  clearInventarioResumenFilters,
} from "../../../slices/biSlice";

const API_URL = process.env.REACT_APP_API_URL;
const POLLING_INTERVAL = 900000; // 15 minutos

/**
 * Vista Simple de Inventario (Requirement 12).
 * Una fila por producto con métricas por bodega: inventario físico, tránsito,
 * vendido últimos 30 días y vendido histórico (solo ventas pagadas).
 * Pensada para uso operativo sin parámetros calculados.
 */
export function InventarioSimplePage() {
  const dispatch = useDispatch();
  const { data, bodegas, total, loading, error, lastUpdated, filters, pagination } =
    useSelector((state) => state.bi.inventarioResumen);
  const token = useSelector((state) => state.app.user?.token);

  // Dynamic filter options
  const [marcaOptions, setMarcaOptions] = useState([]);
  const [proveedorOptions, setProveedorOptions] = useState([]);
  const [categoriaOptions, setCategoriaOptions] = useState([]);

  // Fetch filter options on mount
  useEffect(() => {
    if (!token) return;
    const headers = { "Content-type": "application/json", Authorization: `Bearer ${token}` };

    fetch(`${API_URL}/marcas`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) =>
        setMarcaOptions(
          data
            .map((m) => ({ value: m.nombre, label: m.nombre }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});

    fetch(`${API_URL}/proveedores`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) =>
        setProveedorOptions(
          data
            .map((p) => ({ value: String(p.id), label: p.nombre }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});

    fetch(`${API_URL}/categorias`, { headers })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) =>
        setCategoriaOptions(
          data
            .map((c) => ({ value: String(c.id), label: c.nombre || `Categoría ${c.id}` }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});
  }, [token]);

  // Polling: refrescar cada 15 min
  const fetchData = useCallback(() => {
    return dispatch(fetchInventarioResumen());
  }, [dispatch]);

  const { lastUpdated: pollingLastUpdated, isStale, refetch } = usePolling(
    fetchData,
    POLLING_INTERVAL
  );

  const displayLastUpdated = lastUpdated || pollingLastUpdated;

  // ============================================================
  // Configuración de filtros para FilterBar
  // ============================================================
  const filterConfig = useMemo(
    () => [
      {
        name: "busqueda",
        label: "Buscar",
        type: "text",
        placeholder: "Nombre, marca o código...",
      },
      {
        name: "marca",
        label: "Marca",
        type: "select",
        options: marcaOptions,
        placeholder: "Todas las marcas",
      },
      {
        name: "proveedor",
        label: "Proveedor",
        type: "select",
        options: proveedorOptions,
        placeholder: "Todos los proveedores",
      },
      {
        name: "categoria",
        label: "Categoría",
        type: "select",
        options: categoriaOptions,
        placeholder: "Todas las categorías",
      },
    ],
    [marcaOptions, proveedorOptions, categoriaOptions]
  );

  // ============================================================
  // Handlers
  // ============================================================
  const debounceRef = useRef(null);

  const handleFilterChange = useCallback(
    (name, value) => {
      dispatch(setInventarioResumenFilters({ [name]: value || null }));

      if (name === "busqueda") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          dispatch(fetchInventarioResumen());
        }, 400);
      } else {
        dispatch(fetchInventarioResumen());
      }
    },
    [dispatch]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(clearInventarioResumenFilters());
    dispatch(fetchInventarioResumen());
  }, [dispatch]);

  const handlePageChange = useCallback(
    (skip) => {
      dispatch(setInventarioResumenPagination({ skip }));
      dispatch(fetchInventarioResumen());
    },
    [dispatch]
  );

  const handlePageSizeChange = useCallback(
    (limit) => {
      dispatch(setInventarioResumenPagination({ limit, skip: 0 }));
      dispatch(fetchInventarioResumen());
    },
    [dispatch]
  );

  // ============================================================
  // Export URL
  // ============================================================
  const exportUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val != null && val !== "") {
        params.append(key, val);
      }
    });
    if (token) params.append("access_token", token);
    const qs = params.toString();
    return `${API_URL}/bi/inventario/resumen/export/excel${qs ? `?${qs}` : ""}`;
  }, [filters, token]);

  // ============================================================
  // Render
  // ============================================================
  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-1">
        <h1 className="text-xl font-bold text-gray-800">Inventario Simple</h1>
        <div className="flex items-center gap-3 ml-auto">
          <PollingIndicator
            lastUpdated={displayLastUpdated}
            isStale={isStale}
            onRefresh={refetch}
          />
          <ExportButtons exportUrl={exportUrl} />
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Una fila por producto y, para cada bodega: <strong>Inv</strong> =
        inventario físico (ya descuenta los pedidos ingresados),{" "}
        <strong>Tráns</strong> = unidades en camino hacia esa bodega,{" "}
        <strong>Vend 30d</strong> = vendido y pagado en los últimos 30 días,{" "}
        <strong>Vend Hist</strong> = vendido y pagado desde el inicio.
      </p>

      {/* Error / stale data message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm">
            Error al cargar datos: {error}.
            {displayLastUpdated && <> Mostrando última consulta exitosa.</>}
          </span>
          <button
            onClick={refetch}
            className="ml-auto text-sm text-red-700 underline hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Filtros */}
      <FilterBar
        filters={filterConfig}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* Tabla de datos */}
      <GroupedInventoryTable
        data={data}
        bodegas={bodegas}
        loading={loading}
        emptyMessage="No hay datos de inventario disponibles."
      />

      {/* Paginación */}
      {pagination && total > 0 && (
        <PaginationControls
          limit={pagination.limit}
          skip={pagination.skip}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageOptions={[50, 100, 200]}
        />
      )}
    </div>
  );
}

export default InventarioSimplePage;
